import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { FileText, CheckCircle, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageLayout from '../components/PageLayout';
import NotApplicable from '../components/NotApplicable';
import { useSiteSettings } from '../SiteSettingsContext';

const DEFAULT_WAIVER_TEXT = `WAIVER AND RELEASE OF LIABILITY
Guatemala Surgery Surgical Missions

1. Voluntary Participation & Standards of Conduct
I acknowledge that my participation in a Guatemala Surgery / Common Hope surgical mission is entirely voluntary.`;

const DEFAULT_PLAIN_SUMMARY = `You are volunteering by choice and agree to follow mission rules.
International medical missions involve real risks, including illness, injury, travel hazards, and limited medical access.
You accept those risks and agree not to hold Guatemala Surgery or Common Hope legally responsible unless the law requires otherwise.
You are responsible for your own health, medications, insurance, and expenses.
This waiver is valid for the current calendar year only.`;

/**
 * Smart format waiver text — auto-bolds section numbers and titles.
 */
const formatWaiverText = (text) => {
  if (!text) return '';
  return text.split('\n').map((line, i) => {
    const trimmed = line.trim();
    // Bold section headers (lines starting with a number followed by a period)
    if (/^\d+\./.test(trimmed)) {
      return <p key={i} className="font-bold text-slate-800 mt-4 mb-1">{trimmed}</p>;
    }
    // Bold the title (first non-empty line)
    if (i === 0 && trimmed) {
      return <h3 key={i} className="text-lg font-bold text-slate-800 mb-1">{trimmed}</h3>;
    }
    // Subtitle (second line)
    if (i === 1 && trimmed) {
      return <p key={i} className="text-sm text-slate-500 mb-4">{trimmed}</p>;
    }
    if (!trimmed) return <br key={i} />;
    return <p key={i} className="mb-2">{trimmed}</p>;
  });
};

const WaiverPage = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [waiverStatus, setWaiverStatus] = useState({ signed: false, signed_date: null });
  const [agreed, setAgreed] = useState(false);
  const [agreedElectronic, setAgreedElectronic] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [myTrips, setMyTrips] = useState([]);

  // Addendum state
  const [addendumStatus, setAddendumStatus] = useState(null);
  const [addendumCheck1, setAddendumCheck1] = useState(false);
  const [addendumCheck2, setAddendumCheck2] = useState(false);
  const [addendumCheck3, setAddendumCheck3] = useState(false);
  const [addendumRelationship, setAddendumRelationship] = useState('Parent');
  const [signingGuardianAddendum, setSigningGuardianAddendum] = useState(false);
  const [signingMinorAck, setSigningMinorAck] = useState(false);
  const [isGuardianSession, setIsGuardianSession] = useState(false);

  const WAIVER_TEXT = settings.waiver_text || DEFAULT_WAIVER_TEXT;
  const plainSummaryText = settings.waiver_plain_summary || DEFAULT_PLAIN_SUMMARY;
  const PLAIN_SUMMARY_BULLETS = plainSummaryText.split('\n').filter(l => l.trim());

  useEffect(() => {
    fetchWaiverStatus();
    api.get('/my-trips').then(res => setMyTrips(res.data || [])).catch(() => {});
    // Fetch addendum status
    api.get('/waiver/addendum/status').then(res => setAddendumStatus(res.data)).catch(() => {});
    // Check guardian session
    api.get('/auth/me').then(res => {
      if (res.data?.guardian_session) setIsGuardianSession(true);
    }).catch(() => {});
  }, []);

  const fetchWaiverStatus = async () => {
    try {
      const res = await api.get('/waiver/status');
      setWaiverStatus(res.data);
    } catch (error) {
      console.error('Error fetching waiver status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (e) => {
    e.preventDefault();
    
    if (!agreed) {
      toast.error('Please agree to the waiver terms first');
      return;
    }

    if (!agreedElectronic) {
      toast.error('Please consent to the use of electronic records');
      return;
    }
    
    if (!signatureName.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    setSigning(true);
    try {
      await api.post('/waiver/sign', { signature_name: signatureName });
      toast.success('Waiver signed successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to sign waiver');
    } finally {
      setSigning(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const headerStatus = waiverStatus.signed ? (
    <div className="flex items-center gap-2 text-status-success">
      <CheckCircle className="w-5 h-5" />
      <span className="font-medium">Signed</span>
    </div>
  ) : null;

  const hasTrip = myTrips.length > 0;

  return (
    <PageLayout title="Liability Waiver" headerActions={headerStatus} maxWidth="max-w-3xl">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !hasTrip ? (
            <NotApplicable
              title="Waiver Not Required Yet"
              message="The liability waiver will need to be signed once you are assigned to a trip."
              linkTo="/dashboard"
            />
          ) : waiverStatus.signed ? (
            <div className="space-y-6">
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-status-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-status-success" />
                  </div>
                  <h2 className="text-xl font-heading font-bold text-slate-800 mb-2">
                    Waiver Signed
                  </h2>
                  <p className="text-slate-500 mb-4">
                    Signed on {new Date(waiverStatus.signed_date).toLocaleDateString()} · Valid through December 31, {waiverStatus.expires_year}
                  </p>
                  <Link to="/dashboard">
                    <Button className="rounded-full bg-rojo hover:bg-rojo-dark">Back to Dashboard</Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Full waiver text for reference */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Waiver Text (for reference)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] rounded-md border p-4">
                    <div className="prose prose-sm max-w-none text-slate-700 text-sm leading-relaxed">
                      {formatWaiverText(WAIVER_TEXT)}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          ) : (
            <form noValidate onSubmit={handleSign}>
              {/* 1. Main Waiver Text */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Waiver and Release of Liability
                  </CardTitle>
                  <CardDescription>
                    Please read the following waiver carefully before signing.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-96 rounded-lg border border-slate-200 p-4 bg-slate-50">
                    <div className="text-sm text-slate-700 font-sans leading-relaxed">
                      {formatWaiverText(WAIVER_TEXT)}
                    </div>
                    <div className="mt-6 pt-5 border-t border-slate-300">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                        Plain-English Summary for Volunteers
                        <span className="ml-2 font-normal normal-case text-slate-400">(Not a substitute for the waiver above)</span>
                      </p>
                      <ul className="space-y-2">
                        {PLAIN_SUMMARY_BULLETS.map((point, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* 2. Addendum Text — minor only */}
              {addendumStatus?.is_minor && (
                <Card className="mb-6 border-purple-200">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Shield className="w-5 h-5 text-purple-600" />
                      Addendum — Minor Participant
                    </CardTitle>
                    <CardDescription>
                      This addendum is required because the participant is under 18.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <ScrollArea className="h-72 rounded-lg border border-purple-200 p-4 bg-purple-50/30">
                      <div className="text-sm text-slate-700 leading-relaxed space-y-3">
                        <h3 className="font-bold text-slate-800">ADDENDUM TO WAIVER AND RELEASE OF LIABILITY</h3>
                        <p className="italic text-slate-600">Participation by a Minor; Execution by Parent or Legal Guardian</p>
                        <p className="font-semibold">Guatemala Surgery Surgical Missions</p>
                        <p>This Addendum applies whenever the Participant identified in the foregoing Waiver and Release of Liability (the "Agreement") is under the age of 18 (a "Minor"). If the Participant is a Minor, the Agreement has no legal effect as to the Minor's participation unless it is read, understood, and signed below by the Minor's parent or legal guardian.</p>
                        <p>By signing below, the undersigned represents, warrants, and agrees that:</p>
                        <p>(a) the undersigned is the Minor's parent or legal guardian and has full legal authority to consent to the Minor's participation and to bind the Minor to the terms of the Agreement;</p>
                        <p>(b) the undersigned is signing the Agreement both individually and on behalf of the Minor, and by doing so agrees to all terms of the Agreement — including the assumption of risk, release and hold harmless, and indemnification provisions — on the Minor's behalf and for the Minor, to the fullest extent permitted by applicable law;</p>
                        <p>(c) all references in the Agreement to Losses, Indemnified Parties, and other defined terms apply equally to Losses suffered by, or claims arising out of or relating to, the Minor's participation;</p>
                        <p>(d) in consideration of the Minor being permitted to participate, the undersigned also individually releases, waives, discharges, and covenants not to sue Guatemala Surgery and Common Hope, and agrees individually to indemnify, hold harmless, and defend Guatemala Surgery and Common Hope, for any and all Losses arising out of or related to the Minor's participation, to the extent permitted by law; and</p>
                        <p>(e) the Agreement is intended to bind the Minor, the undersigned, and their respective heirs, executors, administrators, and legal representatives, to the maximum extent permitted under applicable law.</p>
                        <p>The undersigned confirms being at least 18 years of age and competent to sign this Addendum, and confirms having read and understood the Agreement in its entirety, including the description of risks associated with the mission, and voluntarily agreeing to assume those risks on behalf of the Minor.</p>
                      </div>
                    </ScrollArea>

                    {/* 3. Minor Acknowledgment */}
                    <div className="border-t border-purple-200 pt-4">
                      <h4 className="font-heading font-semibold text-purple-800 mb-2">Minor Participant Acknowledgment</h4>
                      <p className="text-sm text-slate-600 mb-3 italic">
                        "I am the minor participant named above. I have read (or had read to me) and understand the risks described in the Agreement, and I voluntarily agree to participate."
                      </p>
                      {addendumStatus.minor_signed ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-green-800">Signed by {addendumStatus.minor_name}</p>
                            <p className="text-xs text-green-600">{new Date(addendumStatus.minor_signed_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ) : addendumStatus.age >= 15 && !isGuardianSession ? (
                        <Button
                          type="button"
                          className="rounded-full bg-purple-600 hover:bg-purple-700"
                          disabled={signingMinorAck}
                          onClick={async () => {
                            setSigningMinorAck(true);
                            try {
                              await api.post('/waiver/addendum/minor-sign');
                              toast.success('Minor acknowledgment signed');
                              const res = await api.get('/waiver/addendum/status');
                              setAddendumStatus(res.data);
                            } catch (e) { toast.error(e.response?.data?.detail || 'Failed to sign'); }
                            finally { setSigningMinorAck(false); }
                          }}
                          data-testid="sign-minor-ack-btn"
                        >
                          {signingMinorAck ? 'Signing...' : 'Sign Acknowledgment'}
                        </Button>
                      ) : addendumStatus.age < 15 && isGuardianSession ? (
                        <Button
                          type="button"
                          className="rounded-full bg-purple-600 hover:bg-purple-700"
                          disabled={signingMinorAck}
                          onClick={async () => {
                            setSigningMinorAck(true);
                            try {
                              await api.post('/waiver/addendum/guardian-sign-for-minor');
                              toast.success('Signed on behalf of minor');
                              const res = await api.get('/waiver/addendum/status');
                              setAddendumStatus(res.data);
                            } catch (e) { toast.error(e.response?.data?.detail || 'Failed to sign'); }
                            finally { setSigningMinorAck(false); }
                          }}
                          data-testid="sign-minor-ack-guardian-btn"
                        >
                          {signingMinorAck ? 'Signing...' : 'Sign on Behalf of Minor (under 15)'}
                        </Button>
                      ) : (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                          <p className="text-sm text-amber-700">
                            {addendumStatus.age < 15
                              ? 'Minor is under 15 — the guardian must sign this section on their behalf.'
                              : 'The minor must log in to their own account to sign this acknowledgment.'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* 4. Guardian Addendum Checkboxes + Relationship */}
                    <div className="border-t border-purple-200 pt-4">
                      <h4 className="font-heading font-semibold text-purple-800 mb-3">Guardian Confirmation</h4>
                      {addendumStatus.guardian_signed ? (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-green-800">Confirmed by {addendumStatus.guardian_name}</p>
                            <p className="text-xs text-green-600">{addendumStatus.guardian_relationship} · {new Date(addendumStatus.guardian_signed_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ) : isGuardianSession ? (
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <Checkbox id="addendum1" checked={addendumCheck1} onCheckedChange={setAddendumCheck1} className="mt-0.5" data-testid="addendum-check-1" />
                            <Label htmlFor="addendum1" className="cursor-pointer leading-snug text-sm">I confirm that I am the parent or legal guardian of the Minor named below.</Label>
                          </div>
                          <div className="flex items-start gap-3">
                            <Checkbox id="addendum2" checked={addendumCheck2} onCheckedChange={setAddendumCheck2} className="mt-0.5" data-testid="addendum-check-2" />
                            <Label htmlFor="addendum2" className="cursor-pointer leading-snug text-sm">I have read, understood, and agree to be bound by the Waiver and Release of Liability, on my own behalf and on behalf of the Minor.</Label>
                          </div>
                          <div className="flex items-start gap-3">
                            <Checkbox id="addendum3" checked={addendumCheck3} onCheckedChange={setAddendumCheck3} className="mt-0.5" data-testid="addendum-check-3" />
                            <Label htmlFor="addendum3" className="cursor-pointer leading-snug text-sm">I am signing voluntarily and of my own free will.</Label>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Relationship to Minor</Label>
                            <Select value={addendumRelationship} onValueChange={setAddendumRelationship}>
                              <SelectTrigger className="w-48" data-testid="addendum-relationship"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['Parent', 'Legal Guardian', 'Stepparent', 'Grandparent', 'Other'].map(r => (
                                  <SelectItem key={r} value={r}>{r}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            className="rounded-full bg-purple-600 hover:bg-purple-700"
                            disabled={!addendumCheck1 || !addendumCheck2 || !addendumCheck3 || signingGuardianAddendum}
                            onClick={async () => {
                              setSigningGuardianAddendum(true);
                              try {
                                await api.post('/waiver/addendum/guardian-sign', { relationship: addendumRelationship });
                                toast.success('Guardian addendum confirmed');
                                const res = await api.get('/waiver/addendum/status');
                                setAddendumStatus(res.data);
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to sign'); }
                              finally { setSigningGuardianAddendum(false); }
                            }}
                            data-testid="sign-guardian-addendum-btn"
                          >
                            {signingGuardianAddendum ? 'Confirming...' : 'Confirm as Guardian'}
                          </Button>
                        </div>
                      ) : (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                          <p className="text-sm text-amber-700">Awaiting guardian confirmation. The guardian must log in and complete this section.</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* 5. Digital Signature Card */}
              {addendumStatus?.is_minor && !isGuardianSession ? (
                <Card className="border-amber-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-amber-800">Guardian Signature Required</p>
                        <p className="text-sm text-amber-600 mt-1">The waiver must be signed by your guardian. Please ask your guardian to log in and sign.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading">Digital Signature</CardTitle>
                    <CardDescription>
                      {addendumStatus?.is_minor
                        ? 'By signing below, you acknowledge that you have read the waiver and the addendum above, and agree to all terms on behalf of the minor.'
                        : 'By signing below, you acknowledge that you have read and agree to the terms above.'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-start gap-3 p-4 bg-status-warning/5 border border-status-warning/20 rounded-xl">
                      <AlertTriangle className="w-5 h-5 text-status-warning mt-0.5" />
                      <div className="text-sm text-slate-600">
                        <p className="font-medium text-slate-800 mb-1">Important</p>
                        <p>This is a legally binding document. Please ensure you understand all terms before signing.</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Checkbox 
                        id="agree"
                        checked={agreed}
                        onCheckedChange={setAgreed}
                        data-testid="agree-checkbox"
                      />
                      <Label htmlFor="agree" className="cursor-pointer">
                        I have read and agree to the Waiver and Release of Liability{addendumStatus?.is_minor ? ' and the Minor Addendum' : ''}
                      </Label>
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="agree-electronic"
                        checked={agreedElectronic}
                        onCheckedChange={setAgreedElectronic}
                        data-testid="agree-electronic-checkbox"
                        className="mt-0.5"
                      />
                      <Label htmlFor="agree-electronic" className="cursor-pointer leading-snug">
                        I consent to the use of electronic records and an electronic signature for this agreement. I understand my typed name carries the same legal effect as a handwritten signature.
                      </Label>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signature">Full Legal Name (as signature)</Label>
                      <Input
                        id="signature"
                        data-testid="signature-input"
                        value={signatureName}
                        onChange={(e) => setSignatureName(e.target.value)}
                        placeholder="Type your full legal name"
                        className="text-lg"
                      />
                      <p className="text-xs text-slate-500">
                        Today's date: {new Date().toLocaleDateString()}
                      </p>
                    </div>

                    <div className="flex justify-end gap-4 pt-4">
                      <Link to="/dashboard">
                        <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
                      </Link>
                      <Button 
                        type="submit"
                        className="rounded-full bg-rojo hover:bg-rojo-dark"
                        disabled={!agreed || !agreedElectronic || !signatureName.trim() || signing}
                        data-testid="sign-waiver-btn"
                      >
                        {signing ? 'Signing...' : 'Sign Waiver'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </form>
          )}
    </PageLayout>
  );
};

export default WaiverPage;
